import { Currency } from '@/constants/currency';
import { AnalyticsContext, AnalyticsEventName } from './constants';
import { trackEvent } from './utils';
import {
  Address,
  CheckoutResponse,
  ICartViewModel,
  ItemAnalyticsModel,
  ItemListAnalyticsModel,
  OrderLineViewModel
} from '@/@types/client-api';
import {
  ConvertCartToItemAnalyticsModels,
  ConvertItemAnalyticsModel,
  ConvertItemAnalyticsModelBase,
  type ItemAnalyticsParams
} from './analyticsUtils';
import { IPaymentOption } from '@/components/ui/CheckoutAndOrderManagement/hooks/useCheckoutHook';
import { IContent } from '@/@types/cms';

const generateEcommerceObject = (data: object) => ({
  ecommerce: { ...data }
});

const flattenJoinWaitlistItemParams = (item: ItemAnalyticsParams | undefined) => {
  if (!item) return {};

  const params = {
    affiliation: item.affiliation,
    coupon: item.coupon,
    discount: item.discount,
    index: item.index,
    item_brand: item.item_brand,
    item_category: item.item_category,
    item_category2: item.item_category2,
    item_category3: item.item_category3,
    item_category4: item.item_category4,
    item_category5: item.item_category5,
    item_id: item.item_id,
    item_list_id: item.item_list_id,
    item_list_name: item.item_list_name,
    item_list_price: item.item_list_price,
    item_location: item.location_id,
    item_location_id: item.location_id,
    item_name: item.item_name,
    parent_id: item.parentId,
    parent_name: item.parentName,
    price: item.price,
    quantity: 1
  };

  return Object.entries(params).reduce<Record<string, unknown>>((acc, [key, value]) => {
    acc[key] = typeof value === 'string' ? value.substring(0, 100) : value;
    return acc;
  }, {});
};

const LAST_SELECT_ITEM_CONTEXT_KEY = 'last_select_item_context';

type LastSelectItemContext = {
  itemId?: string;
  parentId?: string;
  listId?: string;
  listName?: string;
  index?: number;
  timestamp: number;
};

const getStoredSelectItemContext = () => {
  try {
    const raw = sessionStorage.getItem(LAST_SELECT_ITEM_CONTEXT_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as LastSelectItemContext;
  } catch {
    return undefined;
  }
};

const resolveItemListAnalyticsModel = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel
): ItemListAnalyticsModel | undefined => {
  const hasProvidedList = !!itemListAnalyticsModel?.listId || !!itemListAnalyticsModel?.listName;
  if (hasProvidedList) return itemListAnalyticsModel;

  const parsed = getStoredSelectItemContext();
  const sameItem =
    (!!itemAnalyticsModel?.id &&
      (parsed?.itemId === itemAnalyticsModel.id || parsed?.parentId === itemAnalyticsModel.id)) ||
    (!!itemAnalyticsModel?.parentId && parsed?.itemId === itemAnalyticsModel.parentId);
  if (!sameItem) return itemListAnalyticsModel;

  return {
    listId: parsed?.listId || '',
    listName: parsed?.listName || ''
  };
};

const resolveItemIndex = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (typeof itemIndex === 'number' && itemIndex >= 0) return itemIndex;

  const parsed = getStoredSelectItemContext();
  const sameItem =
    (!!itemAnalyticsModel?.id &&
      (parsed?.itemId === itemAnalyticsModel.id || parsed?.parentId === itemAnalyticsModel.id)) ||
    (!!itemAnalyticsModel?.parentId && parsed?.itemId === itemAnalyticsModel.parentId);
  const sameList =
    !itemListAnalyticsModel?.listId || parsed?.listId === itemListAnalyticsModel.listId;

  if (sameItem && sameList && typeof parsed?.index === 'number' && parsed.index >= 0) {
    return parsed.index;
  }

  return undefined;
};

export const resolveItemAnalyticsContext = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  const resolvedListModel = resolveItemListAnalyticsModel(
    itemAnalyticsModel,
    itemListAnalyticsModel
  );
  const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

  return {
    itemListAnalyticsModel: resolvedListModel,
    itemIndex: resolvedItemIndex
  };
};

export interface PromoEvent {
  creative_name?: string;
  creative_slot?: string;
  location_id?: string;
  promotion_id?: string;
  promotion_name?: string;
  context?: AnalyticsContext;
}

export interface PlaceOrderEvent {
  cart?: ICartViewModel;
  context?: AnalyticsContext;
  response?: CheckoutResponse;
  shippingAddress?: Address;
  upkeep?: boolean;
  isStoredInstrument?: boolean;
  saveForFutureUse?: boolean;
  isPaymentRequired?: boolean;
}

export const trackSelectItem = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  listId?: string,
  listName?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel && context) {
    const fallbackListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedListId =
      listId || itemListAnalyticsModel?.listId || fallbackListModel?.listId || '';
    const resolvedListName =
      listName || itemListAnalyticsModel?.listName || fallbackListModel?.listName || '';
    const resolvedListModel: ItemListAnalyticsModel = {
      listId: resolvedListId,
      listName: resolvedListName
    };
    const resolvedItemIndex =
      resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex) ?? 0;

    if (resolvedListId || resolvedListName || typeof itemIndex === 'number') {
      const selectContext: LastSelectItemContext = {
        itemId: itemAnalyticsModel?.id ?? undefined,
        parentId: itemAnalyticsModel?.parentId ?? undefined,
        listId: resolvedListId,
        listName: resolvedListName,
        index: resolvedItemIndex,
        timestamp: Date.now()
      };

      try {
        sessionStorage.setItem(LAST_SELECT_ITEM_CONTEXT_KEY, JSON.stringify(selectContext));
      } catch {
        // no-op
      }
    }

    trackEvent({
      eventName: AnalyticsEventName.SelectItem,
      parameters: {
        context: context || '',
        ...generateEcommerceObject({
          item_list_id: resolvedListId,
          item_list_name: resolvedListName,
          ...ConvertItemAnalyticsModel(itemAnalyticsModel, 1, resolvedListModel, resolvedItemIndex)
        })
      }
    });
  }
};

export const trackAddToCart = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  transactionId?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    trackEvent({
      eventName: AnalyticsEventName.AddToCart,
      parameters: {
        ...generateEcommerceObject({
          value: itemAnalyticsModel?.price || 0,
          transaction_id: transactionId || '',
          currency: Currency.USD,
          items: [
            ConvertItemAnalyticsModelBase(itemAnalyticsModel, resolvedListModel, resolvedItemIndex)
          ]
        }),
        context: context || ''
      }
    });
  }
};

export const buildJoinWaitlistEventData = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);
    const item = ConvertItemAnalyticsModelBase(
      itemAnalyticsModel,
      resolvedListModel,
      resolvedItemIndex
    );

    return {
      ...flattenJoinWaitlistItemParams(item),
      context: context || ''
    };
  }
};

export const trackBeginCheckoutEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  transactionId?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    trackEvent({
      eventName: AnalyticsEventName.BeginCheckout,
      parameters: {
        ...generateEcommerceObject({
          transaction_id: transactionId || '',
          items: [
            ConvertItemAnalyticsModelBase(itemAnalyticsModel, resolvedListModel, resolvedItemIndex)
          ]
        }),
        context: context || ''
      }
    });
  }
};

export const trackPurchaseEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  transactionId?: string,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  isStoredInstrument?: boolean,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    trackEvent({
      eventName: AnalyticsEventName.Purchase,
      parameters: {
        ...generateEcommerceObject({
          transaction_id: transactionId || '',
          items: [
            ConvertItemAnalyticsModelBase(itemAnalyticsModel, resolvedListModel, resolvedItemIndex)
          ]
        }),
        context: context || '',
        purchase_source:
          itemAnalyticsModel?.price && itemAnalyticsModel?.price > 0 ? 'Checkout' : 'WithRequest',
        stored_instrument: isStoredInstrument ?? false
      }
    });
  }
};

export const trackViewCartEvent = (cart?: ICartViewModel, context?: AnalyticsContext) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    trackEvent({
      eventName: AnalyticsEventName.ViewCart,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          value: cart?.subTotal,
          currency: Currency.USD
        }),
        context: context || ''
      }
    });
  }
};

export const trackSelectCartEvent = (context?: AnalyticsContext) => {
  if (context) {
    trackEvent({
      eventName: AnalyticsEventName.SelectCart,
      parameters: {
        context: context
      }
    });
  }
};

export const trackPrepareCheckout = (cart?: ICartViewModel, transactionId?: string) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.BeginCheckout,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          transaction_id: transactionId ?? '',
          value: cart?.subTotal,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? ''
        }),
        context: AnalyticsContext.CheckoutPage
      }
    });
  }
};

export const trackAddPaymentInfo = (cart?: ICartViewModel, paymentOption?: IPaymentOption) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.AddPaymentInfo,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          value: cart?.subTotal,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? '',
          payment_type: paymentOption?.cardType
        }),
        stored_instrument: paymentOption ? paymentOption.sk !== -1 : false,
        save_for_future_use: paymentOption?.saveForFutureUse ?? false,
        default: paymentOption?.isDefault ?? false
      }
    });
  }
};

export const trackAddAddressInfo = (
  cart?: ICartViewModel,
  type?: 'billing' | 'shipping',
  isDefault?: boolean,
  isStoredAddress?: boolean
) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName:
        type === 'shipping'
          ? AnalyticsEventName.AddShippingInfo
          : AnalyticsEventName.AddBillingInfo,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(cart),
          value: cart?.subTotal,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? '',
          ...(type === 'shipping' && { shipping_tier: 'standard' }),
          ...(type === 'billing' && { payment_type: 'CreditCard CyberSource' })
        }),
        default: isDefault ?? false,
        stored_address: isStoredAddress ?? true
      }
    });
  }
};

export const trackApplyPromoCode = (
  cart?: ICartViewModel,
  context?: AnalyticsContext,
  success?: boolean,
  discount?: number
) => {
  if ((cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.ApplyCodeStart,
      parameters: {
        ...generateEcommerceObject({
          items:
            analytics.map((x, index) => ConvertItemAnalyticsModelBase(x, undefined, index)) ?? [],
          coupon: analytics.find(x => x.coupon)?.coupon ?? ''
        }),
        code_success: success ?? false,
        discount: discount ?? 0,
        context: context ?? ''
      }
    });
  }
};

export const trackPlaceOrderEvent = (event: PlaceOrderEvent) => {
  if ((event.cart?.orderLines?.length ?? 0) > 0) {
    const analytics =
      event.cart?.orderLines?.map(x => x.itemAnalyticsModel).filter(x => x !== undefined) ?? [];

    trackEvent({
      eventName: AnalyticsEventName.Purchase,
      parameters: {
        ...generateEcommerceObject({
          items: ConvertCartToItemAnalyticsModels(event.cart),
          transaction_id: event.response?.orderNumber || '',
          value: event.cart?.total,
          tax: event.cart?.taxTotal,
          shipping: event.cart?.shippingCost,
          currency: Currency.USD,
          coupon: analytics.find(x => x.coupon)?.coupon ?? ''
        }),
        upkeep: event.upkeep ?? false,
        poec: event.cart?.hasPoec ?? false,
        international_shipping: false,
        rush_shipping_required: false,
        shipping_tier: 'Standard',
        payment_type: 'CreditCard CyberSource',
        shipping_city: event.shippingAddress?.city ?? '',
        shipping_state: event.shippingAddress?.state ?? '',
        shipping_country: event.shippingAddress?.country ?? '',
        shipping_zip: event.shippingAddress?.zip ?? '',
        shipping_zip4: event.shippingAddress?.plus4 ?? '',
        shipping_address_category: event.shippingAddress?.addressCategory ?? '',
        stored_instrument: event.isStoredInstrument ?? false,
        save_for_future_use: event.saveForFutureUse ?? false,
        total_net_price: event.cart?.total ?? '',
        total_discount: event.cart?.discountValue ?? '',
        purchase_source: 'Checkout',
        zero_dollar_purchase: !event.isPaymentRequired,
        upkeep_service_option: event.cart?.hasUpkeepItems ?? false,
        context: event.context || ''
      }
    });
  }
};

export const trackViewItemEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    trackEvent({
      eventName: AnalyticsEventName.ViewItem,
      parameters: {
        ...generateEcommerceObject({
          item_list_id: resolvedListModel?.listId || '',
          item_list_name: resolvedListModel?.listName || '',
          ...ConvertItemAnalyticsModel(
            itemAnalyticsModel,
            undefined,
            resolvedListModel,
            resolvedItemIndex
          )
        })
      }
    });
  }
};

export const trackViewItemListEvent = (
  listId: string,
  listName: string,
  itemsAnalyticsModel?: ItemAnalyticsModel[],
  context?: AnalyticsContext,
  listStartIndex: number = 0
) => {
  if (itemsAnalyticsModel && itemsAnalyticsModel?.length > 0) {
    trackEvent({
      eventName: AnalyticsEventName.ViewItemList,
      parameters: {
        ...generateEcommerceObject({
          item_list_id: listId,
          item_list_name: listName,
          items: itemsAnalyticsModel?.map((item, index) =>
            ConvertItemAnalyticsModelBase(
              item,
              {
                listId: listId,
                listName: listName
              },
              listStartIndex + index
            )
          )
        }),
        ...(context && { context })
      }
    });
  }
};

export const trackAddWishListEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    const convertedItem = ConvertItemAnalyticsModel(
      itemAnalyticsModel,
      1,
      resolvedListModel,
      resolvedItemIndex
    );

    trackEvent({
      eventName: AnalyticsEventName.AddToWishlist,
      parameters: {
        ...generateEcommerceObject({
          item_list_id: resolvedListModel?.listId || '',
          item_list_name: resolvedListModel?.listName || '',
          ...convertedItem
        }),
        context: context || ''
      }
    });
  }
};

export const trackRemoveWishListEvent = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  context?: AnalyticsContext,
  bulk: boolean = false,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    trackEvent({
      eventName: AnalyticsEventName.RemoveFromWishlist,
      parameters: {
        context: context || '',
        bulk,
        item_list_id: resolvedListModel?.listId || '',
        item_list_name: resolvedListModel?.listName || '',
        items: [
          ConvertItemAnalyticsModelBase(itemAnalyticsModel, resolvedListModel, resolvedItemIndex)
        ]
      }
    });
  }
};

export const trackBulkRemoveWishListEvent = (
  itemsAnalyticsModel?: ItemAnalyticsModel[],
  context?: AnalyticsContext,
  itemListAnalyticsModel?: ItemListAnalyticsModel
) => {
  if (itemsAnalyticsModel && itemsAnalyticsModel.length > 0) {
    trackEvent({
      eventName: AnalyticsEventName.BulkRemoveFromWishlist,
      parameters: {
        context: context || '',
        item_list_id: itemListAnalyticsModel?.listId || '',
        item_list_name: itemListAnalyticsModel?.listName || '',
        items: itemsAnalyticsModel.map((item, index) =>
          ConvertItemAnalyticsModelBase(item, itemListAnalyticsModel, index)
        )
      }
    });
  }
};

export const trackViewPromotionEvent = (
  promotion: PromoEvent,
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    trackEvent({
      eventName: AnalyticsEventName.ViewPromotion,
      parameters: {
        ...generateEcommerceObject({
          creative_name: promotion.creative_name,
          creative_slot: promotion.creative_slot,
          promotion_id: promotion.promotion_id,
          promotion_name: promotion.promotion_name,
          location_id: promotion.location_id,
          items: [
            ConvertItemAnalyticsModelBase(itemAnalyticsModel, resolvedListModel, resolvedItemIndex)
          ]
        }),
        context: promotion.context || ''
      }
    });
  }
};

export const trackSelectPromotionEvent = (
  promotion: PromoEvent,
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (itemAnalyticsModel) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      itemAnalyticsModel,
      itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(itemAnalyticsModel, resolvedListModel, itemIndex);

    trackEvent({
      eventName: AnalyticsEventName.SelectPromotion,
      parameters: {
        ...generateEcommerceObject({
          creative_name: promotion.creative_name,
          creative_slot: promotion.creative_slot,
          promotion_id: promotion.promotion_id,
          promotion_name: promotion.promotion_name,
          items: [
            ConvertItemAnalyticsModelBase(itemAnalyticsModel, resolvedListModel, resolvedItemIndex)
          ],
          location_id: promotion.location_id
        }),
        context: promotion.context || ''
      }
    });
  }
};

export const trackSelectCheckoutEvent = (context?: AnalyticsContext) => {
  if (context) {
    trackEvent({
      eventName: AnalyticsEventName.SelectCheckout,
      parameters: {
        context: context
      }
    });
  }
};

export const trackRemoveFromCartEvent = (
  item?: OrderLineViewModel,
  context?: AnalyticsContext,
  itemIndex?: number
) => {
  if (item && context) {
    const resolvedListModel = resolveItemListAnalyticsModel(
      item.itemAnalyticsModel,
      item.itemListAnalyticsModel
    );
    const resolvedItemIndex = resolveItemIndex(
      item.itemAnalyticsModel,
      resolvedListModel,
      itemIndex
    );

    trackEvent({
      eventName: AnalyticsEventName.RemoveFromCart,
      parameters: {
        ...generateEcommerceObject({
          items: item.itemAnalyticsModel
            ? [
                ConvertItemAnalyticsModelBase(
                  item.itemAnalyticsModel,
                  resolvedListModel,
                  resolvedItemIndex
                )
              ]
            : [],
          value: item.discountPrice ?? 0,
          currency: Currency.USD
        }),
        context: context
      }
    });
  }
};

export const getProductCode = (productItem?: IContent) => {
  const item = productItem?.contentLink?.expanded ?? productItem;
  const code = (item as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' && code.trim() ? code : undefined;
};
